/**
 * What the home page's repeated sections are made of.
 *
 * These are lists of ids that each stand for a pair of locale keys. Kept out of
 * the page so a test can walk them: `t()` falls back to returning the key
 * itself, so a family with one id missing from the locale files renders that
 * id as visible text rather than failing anywhere. siteKeys.test.ts checks the
 * families against all twelve locales.
 */

/** The six things the app does while a call is running: `feat.<id>.t` / `.b`. */
export const FEATURES = ['voice', 'tempo', 'turn', 'terms', 'overlay', 'offline'] as const;

/** What a Kizuna AI account takes on: `carry.<id>.t` / `.b`. */
export const CARRY_OURS = ['account', 'bill', 'cloud', 'packaged'] as const;

/** The exit standing beside each of them. Same key shape. */
export const CARRY_YOURS = ['ownkey', 'direct', 'machine', 'source'] as const;

/** The eight questions, in the order the page asks them: `faq.<id>.q` / `.a`. */
export const FAQ = [
  'audio',
  'believe',
  'other',
  'youtube',
  'service',
  'local',
  'cost',
  'platforms',
] as const;

/**
 * The three platform cards: `download.<id>.title` / `.body`.
 *
 * No ranking is intended by the order — the band's heading no longer claims
 * one platform is first — but it is the order most visitors arrive on, and it
 * matches /download so the two pages do not read as disagreeing.
 */
export const PLATFORMS = ['windows', 'macos', 'linux'] as const;

/**
 * The meeting services the extension is built for. Proper nouns, so they are a
 * constant and not twelve translations of the same nine words. Teams, Gather
 * and Telemost each have several hostnames in the extension's registry; this is
 * the list of services, which is what a visitor is asking about.
 */
export const MEETING_SITES = [
  'Zoom',
  'Google Meet',
  'Microsoft Teams',
  'Slack',
  'Discord',
  'Jitsi Meet',
  'Whereby',
  'Gather',
  'Yandex Telemost',
] as const;

/**
 * The same nine services, named the way /download names them: that page is
 * where someone checks whether their exact Teams or Gather variant is covered,
 * so the variants are spelled out.
 */
export const MEETING_SITES_DETAIL = [
  'Zoom',
  'Google Meet',
  'Microsoft Teams (free, work and M365)',
  'Slack',
  'Discord',
  'Jitsi Meet',
  'Whereby',
  'Gather Town',
  'Yandex Telemost',
] as const;

/**
 * The four figures under "what we carry", with the locale key for the line
 * under each.
 *
 * 35+ translation languages and 10+ providers are the app's own claims; 30 is
 * the count of locale directories in the client (src/locales); the meeting-site
 * figure is derived above so the number and the list cannot disagree.
 */
export const STATS: readonly (readonly [string, string])[] = [
  ['35+', 'stat.langs'],
  ['30', 'stat.ui'],
  [String(MEETING_SITES.length), 'stat.sites'],
  ['10+', 'stat.providers'],
];

/**
 * The one build each platform card on the home page offers. macOS is absent on
 * purpose: which of its builds a visitor needs depends on their Mac's chip.
 */
export const PRIMARY_DOWNLOAD: Record<'linux' | 'windows', string> = {
  linux: '/api/download/linux-deb',
  windows: '/api/download/windows',
};

/**
 * Every build the worker serves, per platform.
 *
 * The labels are package format and architecture — technical identifiers, the
 * same words in every language, so they are literals rather than locale keys.
 * The endpoints match ASSET_PATTERNS in src/index.ts; a label here that names
 * an architecture the release does not carry would 302 to the releases page.
 */
export const DOWNLOADS: Record<
  (typeof PLATFORMS)[number],
  readonly (readonly [string, string])[]
> = {
  windows: [['Installer · x64', '/api/download/windows']],
  macos: [
    ['.pkg · Apple silicon', '/api/download/macos-arm'],
    ['.pkg · Intel', '/api/download/macos-intel'],
  ],
  linux: [
    ['.deb · x64', '/api/download/linux-deb'],
    ['.deb · arm64', '/api/download/linux-deb-arm64'],
    ['AppImage · x64', '/api/download/linux-appimage'],
    ['AppImage · arm64', '/api/download/linux-appimage-arm64'],
  ],
};
